/**
 * The sign-up form.
 *
 * Three things happen here and nothing else: validate loosely, POST once, and
 * say what came back. The server is the authority on whether an address is
 * acceptable — this only catches the typo with no @ in it, so that the round
 * trip is not spent on something visible from the browser.
 */

import { h, fill } from './dom.js';
import { submitWaitlist } from './api.js';

/** Matches the server's rule in `landing-store.js`; kept loose for the same reason. */
const EMAIL_RE = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/**
 * How each server outcome reads to the person who pressed the button.
 *
 * `resurrected` deliberately reads the same as `created`: somebody who
 * unsubscribed and came back does not need to be reminded that they left.
 */
function messageFor(outcome, t) {
  switch (outcome) {
    case 'created':
    case 'resurrected':
      return { tone: 'ok', title: t.okTitle, body: t.okBody };
    case 'resent':
      return { tone: 'ok', title: t.okTitle, body: t.resentBody };
    case 'confirmed':
      return { tone: 'ok', title: t.alreadyTitle, body: t.alreadyBody };
    case 'invalid':
      return { tone: 'bad', title: t.errInvalid, body: '' };
    case 'rate_limited':
      return { tone: 'bad', title: t.errRate, body: '' };
    default:
      return { tone: 'bad', title: t.errGeneric, body: '' };
  }
}

/**
 * The banner shown when a confirmation or unsubscribe link brought the visitor
 * here. Rendered in place of the form, because at that point there is nothing
 * left to submit.
 */
export function linkResultBanner(state, t) {
  const map = {
    confirmed: { tone: 'ok', title: t.confirmedTitle, body: t.confirmedBody },
    unsubscribed: { tone: 'ok', title: t.unsubscribedTitle, body: t.unsubscribedBody },
    expired: { tone: 'bad', title: t.errLinkDead, body: '' }
  };
  const message = map[state];
  if (!message) return null;

  return h('div', { class: `wait__msg wait__msg--${message.tone}`, role: 'status' },
    h('div', { class: 'wait__msgTitle' }, message.title),
    message.body && h('div', { class: 'wait__msgBody' }, message.body)
  );
}

export function waitlistForm({ content, language }) {
  const t = content.waitlist;

  const input = h('input', {
    class: 'wait__input',
    type: 'email',
    name: 'email',
    autocomplete: 'email',
    required: true,
    placeholder: t.placeholder,
    'aria-label': t.title
  });

  /*
   * The honeypot. Named `company` because that is a field bots expect to find
   * and expect to fill; a field called `honeypot` is one they have learned to
   * skip. It is hidden off-screen rather than with display:none, which some
   * form-fillers treat as a signal to leave it alone.
   */
  const trap = h('input', {
    class: 'wait__trap',
    type: 'text',
    name: 'company',
    tabIndex: -1,
    autocomplete: 'off',
    'aria-hidden': 'true'
  });

  const submit = h('button', { class: 'wait__submit', type: 'submit' }, t.submit);
  const status = h('div', { class: 'wait__msg', role: 'status', style: 'display:none' });

  function show(message) {
    status.className = `wait__msg wait__msg--${message.tone}`;
    status.style.display = '';
    fill(status,
      h('div', { class: 'wait__msgTitle' }, message.title),
      message.body ? h('div', { class: 'wait__msgBody' }, message.body) : null
    );
  }

  const form = h('form', {
    class: 'wait__form',
    novalidate: true,
    onsubmit: async event => {
      event.preventDefault();
      const email = input.value.trim();

      if (!EMAIL_RE.test(email)) {
        input.setAttribute('aria-invalid', 'true');
        input.focus();
        show({ tone: 'bad', title: t.errInvalid, body: '' });
        return;
      }
      input.removeAttribute('aria-invalid');

      submit.disabled = true;
      submit.textContent = t.submitting;
      status.style.display = 'none';

      const result = await submitWaitlist({ email, language, company: trap.value });

      submit.disabled = false;
      submit.textContent = t.submit;
      show(messageFor(result.outcome, t));

      /*
       * Cleared only on a success, so a failed attempt does not cost the
       * visitor the address they just typed.
       */
      if (result.outcome !== 'invalid' && result.outcome !== 'rate_limited' && result.outcome !== 'error') {
        input.value = '';
      }
    }
  }, input, trap, submit);

  return { form, status };
}
